import type { CheckoutAppearance, CheckoutEventCallbacks, Mode, WarningCode } from './core.types';
import type { MountOptions } from './contract.types';

export function checkoutError(code: string, message: string): Error & { code: string } {
  return Object.assign(new Error(`[ekko-checkout] ${code}: ${message}`), { code });
}

export interface ResolvedOptions {
  container: HTMLElement;
  mount: MountOptions & { appearance: Partial<CheckoutAppearance> };
  on: Partial<CheckoutEventCallbacks>;
}

type Check = (value: unknown) => boolean;

const OPTION_KEYS = [
  'container',
  'sessionId',
  'clientSecret',
  'sessionData',
  'mode',
  'locale',
  'features',
  'appearance',
  'on',
];

const EVENT_NAMES = ['ready', 'toggleChanged', 'error'];

const oneOf =
  (...values: string[]): Check =>
  (value) =>
    typeof value === 'string' && values.indexOf(value) >= 0;

const isColor: Check = (value) =>
  typeof value === 'string' &&
  value.length <= 64 &&
  !/[;{}\\]|url\(|var\(/i.test(value) &&
  CSS.supports('color', value);

const APPEARANCE: Record<keyof CheckoutAppearance, Check> = {
  theme: oneOf('light', 'dark'),
  image: oneOf('mountain', 'waves', 'elephant', 'bird', 'waterfall'),
  primaryColor: isColor,
  secondaryColor: isColor,
  highlightColor: isColor,
  textFont: (value) => typeof value === 'string' && /^[^;{}()<>\\"']{1,64}$/.test(value),
  outerBorderRadius: (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0,
};

export function warn(code: WarningCode, message: string): void {
  console.warn(`[ekko-checkout] ${code}: ${message}`);
}

const has = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key);

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

export function resolveOptions(options: unknown, modes: Mode[]): ResolvedOptions {
  if (!isObject(options)) throw new Error('[ekko-checkout] initCheckout needs an options object');
  for (const key of ['sessionId', 'clientSecret', 'sessionData']) {
    const value = options[key];
    if (typeof value !== 'string' || !value) throw new Error(`[ekko-checkout] ${key} is required`);
  }

  warnUnknown(options, OPTION_KEYS, '');
  const container = resolveContainer(options.container);

  return {
    container,
    mount: {
      sessionId: options.sessionId as string,
      clientSecret: options.clientSecret as string,
      sessionData: options.sessionData as string,
      locale: resolveLocale(options.locale),
      features: objectOption(options, 'features'),
      appearance: resolveAppearance(objectOption(options, 'appearance')),
      mode: resolveMode(options.mode, modes),
    },
    on: resolveHandlers(objectOption(options, 'on')),
  };
}

function query(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function resolveContainer(container: unknown): HTMLElement {
  const element = typeof container === 'string' ? query(container) : container;
  if (!(element instanceof HTMLElement) || !element.isConnected) {
    throw checkoutError('CONTAINER_NOT_FOUND', `container ${String(container)} is not in the document`);
  }
  return element;
}

function resolveMode(mode: unknown, modes: Mode[]): Mode {
  if (mode === undefined || modes.indexOf(mode as Mode) >= 0) return (mode as Mode) ?? modes[0];
  warn('MODE_MISMATCH', `this core serves ${modes.join(', ')}, not ${String(mode)}.`);
  return modes[0];
}

function ignored(key: string, why: string): undefined {
  warn('INVALID_OPTION', `${key} ${why}; it is ignored.`);
  return undefined;
}

function objectOption(options: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = options[key];
  if (isObject(value)) return value;
  if (value !== undefined) ignored(key, 'must be an object');
  return {};
}

function resolveLocale(locale: unknown): string | undefined {
  if (typeof locale === 'string') return locale || undefined;
  return locale === undefined ? undefined : ignored('locale', 'must be a string');
}

function resolveAppearance(appearance: Record<string, unknown>): Partial<CheckoutAppearance> {
  const valid: Record<string, unknown> = {};
  for (const key of Object.keys(appearance)) {
    const value = appearance[key];
    if (!has(APPEARANCE, key)) unknown(key, Object.keys(APPEARANCE), 'appearance.');
    else if (APPEARANCE[key as keyof CheckoutAppearance](value)) valid[key] = value;
    else ignored(`appearance.${key} ${JSON.stringify(value)}`, 'is invalid');
  }
  return valid;
}

function resolveHandlers(on: Record<string, unknown>): Partial<CheckoutEventCallbacks> {
  warnUnknown(on, EVENT_NAMES, 'on.');
  const handlers: Record<string, unknown> = {};
  for (const name of EVENT_NAMES) {
    if (typeof on[name] === 'function') handlers[name] = on[name];
  }
  return handlers;
}

function warnUnknown(object: Record<string, unknown>, known: string[], prefix: string): void {
  for (const key of Object.keys(object)) {
    if (known.indexOf(key) < 0) unknown(key, known, prefix);
  }
}

function unknown(key: string, known: string[], prefix: string): void {
  const near = nearest(key, known);
  warn(
    'UNKNOWN_OPTION',
    `${prefix}${key} is not an option and is ignored.${near ? ` Did you mean ${prefix}${near}?` : ''}`,
  );
}

export function nearest(key: string, known: string[]): string | undefined {
  let best: string | undefined;
  let bestDistance = Math.max(2, Math.floor(key.length / 3)) + 1;
  for (const candidate of known) {
    const d = distance(key.toLowerCase(), candidate.toLowerCase());
    if (d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  }
  return best;
}

function distance(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        previous[j] + 1,
        row[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = row;
  }
  return previous[b.length];
}
